import { logEvent, setUserId, isSupported } from 'firebase/analytics';
import { Capacitor } from '@capacitor/core';
import { analytics } from '../../firebase';

const APP_OPEN_KEY = 'v2_ga4_app_open';
const CURRENCY = 'INR';

/** @type {boolean|null} */
let analyticsReady = null;

async function ensureAnalytics() {
  if (analyticsReady !== null) return analyticsReady;
  try {
    if (typeof window === 'undefined') {
      analyticsReady = false;
      return false;
    }
    analyticsReady = await isSupported();
  } catch {
    analyticsReady = false;
  }
  return analyticsReady;
}

async function logGa4(eventName, params = {}) {
  try {
    if (!(await ensureAnalytics())) return;
    logEvent(analytics, eventName, params);
  } catch {
    // best-effort
  }
}

function cartLinesToItems(products) {
  return (products || []).map((line, index) => ({
    item_id: String(line.productId ?? line.id ?? index),
    item_name: line.name || 'Item',
    price: Number(line.price) || 0,
    quantity: Number(line.quantity) || 1,
  }));
}

function catalogProductsToItems(products) {
  return (products || []).map((product, index) => {
    const lowestVariant = (product.variants ?? [])
      .filter((v) => v.visibleToCustomer !== false)
      .sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0))[0];
    return {
      item_id: String(product.id ?? index),
      item_name: product.name || 'Item',
      price: Number(lowestVariant?.price ?? product.price) || 0,
      quantity: 1,
    };
  });
}

/**
 * @param {{ path?: string }} opts
 */
export function trackGa4PageView({ path } = {}) {
  const pagePath =
    path || (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '');
  logGa4('page_view', {
    page_path: pagePath,
    page_location: typeof window !== 'undefined' ? window.location.href : undefined,
    page_title: typeof document !== 'undefined' ? document.title : undefined,
  });
}

/**
 * @param {{ platform?: string }} opts
 */
export function trackGa4AppOpen({ platform } = {}) {
  try {
    if (sessionStorage.getItem(APP_OPEN_KEY)) return;
    sessionStorage.setItem(APP_OPEN_KEY, '1');
  } catch {
    // continue without dedupe
  }
  logGa4('app_open', { platform: platform || Capacitor.getPlatform() });
}

export function trackGa4Login() {
  logGa4('login', { method: 'otp' });
}

export function trackGa4SignUp() {
  logGa4('sign_up', { method: 'otp' });
}

/**
 * @param {string|null|undefined} userId
 */
export async function setGa4UserId(userId) {
  try {
    if (!(await ensureAnalytics())) return;
    setUserId(analytics, userId ? String(userId) : null);
  } catch {
    // best-effort
  }
}

/**
 * @param {{ product: object, variant?: object|null }} opts
 */
export function trackGa4ViewItem({ product, variant } = {}) {
  if (!product?.id) return;
  const price = Number(variant?.price ?? product.price) || 0;
  logGa4('view_item', {
    currency: CURRENCY,
    value: price,
    items: [
      {
        item_id: String(product.id),
        item_name: product.name || 'Item',
        price,
        quantity: 1,
      },
    ],
  });
}

/**
 * @param {{ listId?: string, listName?: string, products?: object[], items?: object[] }} opts
 */
export function trackGa4ViewItemList({ listId, listName, products, items } = {}) {
  const ga4Items = items ?? catalogProductsToItems(products);
  if (!ga4Items.length) return;
  logGa4('view_item_list', {
    item_list_id: listId || 'catalog',
    item_list_name: listName || 'Products',
    items: ga4Items,
  });
}

/**
 * @param {{ productId: string|number, name?: string, price?: number, quantity?: number }} opts
 */
export function trackGa4AddToCart({ productId, name, price, quantity = 1 } = {}) {
  const unitPrice = Number(price) || 0;
  const qty = Number(quantity) || 1;
  logGa4('add_to_cart', {
    currency: CURRENCY,
    value: unitPrice * qty,
    items: [
      {
        item_id: String(productId),
        item_name: name || 'Item',
        price: unitPrice,
        quantity: qty,
      },
    ],
  });
}

/**
 * @param {{ cart?: object }} opts
 */
export function trackGa4BeginCheckout({ cart } = {}) {
  const value = Number(cart?.totalAmount) || 0;
  const items = cartLinesToItems(cart?.products);
  if (!items.length) return;
  logGa4('begin_checkout', {
    currency: CURRENCY,
    value,
    items,
  });
}

/**
 * @param {{ orderId: string|number, receipt?: object }} opts
 */
export function trackGa4Purchase({ orderId, receipt } = {}) {
  if (!orderId || receipt?.total == null) return;
  logGa4('purchase', {
    transaction_id: String(orderId),
    affiliation: Capacitor.getPlatform(),
    currency: CURRENCY,
    value: Number(receipt.total) || 0,
    tax: 0,
    shipping: Number(receipt.shippingCost) || 0,
    items: cartLinesToItems(receipt?.products),
  });
}
